import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { rentalAPI } from '../../utils/api';
import { rentalDetailPath } from '../../utils/listingPaths';

function LandlordProfile() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    rentalAPI.getMyRentals()
      .then(res => {
        // 兼容后端返回 { results: [...] } 或直接数组
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setListings(data);
      })
      .catch(() => setError('Failed to load listings'))
      .finally(() => setLoading(false));
  }, []);

  const handleAddListing = () => {
    navigate('/landlord/listing/new');
  };

  const handleEditListing = (listing) => {
    navigate(`/landlord/listing/edit/${listing.id}`);
  };

  const handleDeleteClick = (listing) => {
    setSelectedListing(listing);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedListing) return;
    
    try {
      setLoading(true);
      // 调用后端API删除房源
      await rentalAPI.deleteRental(selectedListing.id);
      
      // 删除成功后更新前端状态
    setListings((prev) => prev.filter((item) => item.id !== selectedListing.id));
    setDeleteDialogOpen(false);
    setSelectedListing(null);
    } catch (error) {
      console.error('Failed to delete rental:', error);
      alert('Failed to delete listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedListing(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 顶部区域 */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4">My Listings</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddListing}
          >
            Add New Listing
          </Button>
        </Box>
      </Box>

      {/* 房源列表 */}
      <Grid container spacing={3}>
        {Array.isArray(listings) && listings.map((listing) => (
          <Grid item xs={12} md={6} key={listing.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
              onClick={() => navigate(rentalDetailPath(listing))}
            >
              <CardMedia
                component="img"
                height="200"
                image={listing.images?.[0] || 'https://source.unsplash.com/random/800x600/?apartment'}
                alt={listing.title}
              />
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" component="h3" sx={{ lineHeight: 1.2 }}>
                    {listing.title}
                  </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {listing.address_line1}
                    {listing.address_line2 ? `, ${listing.address_line2}` : ''}
                    {listing.city ? `, ${listing.city}` : ''}
                    {listing.state ? `, ${listing.state}` : ''}
                    {listing.zip_code ? `, ${listing.zip_code}` : ''}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', mt: 'auto' }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditListing(listing);
                  }}
                  aria-label="edit listing"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(listing);
                  }}
                  aria-label="delete listing"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Listing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedListing?.title}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default LandlordProfile; 